import React, { ReactElement, useState } from 'react';
import Head from 'next/head';

import { Spinner } from 'components';
import { getLayout } from 'components/layouts/Sidebar';
import { Row, Col } from 'components/layout';
import SelectWithInputValue from 'components/SelectWithInputValue';
import AddSelectOptionImage from 'components/AddSelectOptionImage';
import Events from 'components/Events';
import { withAuth } from 'lib/hocs/withAuth';
import { useCompanyProfile } from './UseCompanyProfile.hook';

import styles from './Company.module.scss';
import CompanyDetails from './CompanyDetails';

const Company = (): ReactElement => {
  const {
    company,
    companyBenefits,
    benefits,
    countries,
    remainingCountries,
    companyNationalities,
    addBenefit,
    removeBenefit,
    addNationality,
    removeNationality,
    updateNationality,
    companyInsights,
    onAddInsight,
    onRemoveInsight,
    onCompanyUpdate,
    companyStats,
    addStats,
    removeStats,
  } = useCompanyProfile();

  const [removeBenifitLoadingId, setRemoveBenifitLoadingId] = useState('');
  const [isSaveBenifitLoading, setIsSaveBenifitLoading] = useState(false);
  const [removeCountryLoadingId, setRemoveCountryLoadingId] = useState('');
  const [removeStatsLoadingId, setRemoveStatsLoadingId] = useState('');
  const [isSaveStatsLoading, setIsSaveStatsLoading] = useState(false);

  const onSelectBenefit = (value: string) => {
    addBenefit({ benefit_id: value });
  };

  const onAddBenefit = async (data: { name: string }) => {
    setIsSaveBenifitLoading(true);
    await addBenefit(data);
    setIsSaveBenifitLoading(false);
  };

  const onRemoveBenefit = async (value: string) => {
    setRemoveBenifitLoadingId(value);
    await removeBenefit(value);
    setRemoveBenifitLoadingId('');
  };

  const onRemoveNationality = async (id: string) => {
    setRemoveCountryLoadingId(id);
    await removeNationality(id);
    setRemoveCountryLoadingId('');
  };

  const onRemoveStats = async (value: string) => {
    setRemoveStatsLoadingId(value);
    await removeStats(value);
    setRemoveStatsLoadingId('');
  };

  const onAddStats = async (data: { name: string }) => {
    setIsSaveStatsLoading(true);
    await addStats(data);
    setIsSaveStatsLoading(false);
  };

  return (
    <>
      <Head>
        <title>Company - Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>
      <section className={styles.Company} data-testid="Profile">
        {company ? (
          <div className={styles.CompanyContent}>
            <CompanyDetails {...company} onUpdate={onCompanyUpdate} />
            <div className={styles.CompanySeparator}></div>
            <div className={styles.CompanyBenefits}>
              <p className={styles.CompanyBenefitsTitle}>
                Company Benefits
                <p className={styles.CompanyDesc}>
                  Select the benefits available to your employees
                </p>
              </p>
              <Row gutter={[0, 12]}>
                {companyBenefits.map((benefit) => (
                  <Col key={benefit.id} span={18}>
                    <AddSelectOptionImage
                      onRemove={onRemoveBenefit}
                      options={companyBenefits}
                      value={benefit.value}
                      onChange={onSelectBenefit}
                      isRemoveLoading={removeBenifitLoadingId === benefit.id}
                    ></AddSelectOptionImage>
                  </Col>
                ))}
                <Col span={18}>
                  <AddSelectOptionImage
                    title="Select a benefit"
                    onRemove={onRemoveBenefit}
                    onChange={onSelectBenefit}
                    placeholder="Search for benefit"
                    options={benefits}
                  ></AddSelectOptionImage>
                </Col>
                <Col span={18}>
                  <AddSelectOptionImage
                    options={companyBenefits}
                    onRemove={onRemoveBenefit}
                    onChange={onSelectBenefit}
                    onCustomAdd={onAddBenefit}
                    isSaveLoading={isSaveBenifitLoading}
                    customPlaceholder="Add a custom benefit"
                    isCustom
                  ></AddSelectOptionImage>
                </Col>
              </Row>
            </div>
            <div className={styles.CompanyNationalities}>
              <p className={styles.CompanyNationalitiesTitle}>
                Nationalities
                <p className={styles.CompanyDesc}>
                  What are the nationalities of your current employees?
                </p>
              </p>
              <Row gutter={[0, 12]}>
                {countries.length > 0 &&
                  companyNationalities.map((nationality) => (
                    <Col span={16} key={nationality.id}>
                      <SelectWithInputValue
                        options={countries}
                        selectValue={nationality.country_id}
                        numberValue={nationality.nationality_count}
                        onRemove={() => onRemoveNationality(nationality.id)}
                        onSave={({ numberField }) =>
                          updateNationality(nationality.id, numberField)
                        }
                        isRemoveLoading={
                          removeCountryLoadingId === nationality.id
                        }
                      />
                    </Col>
                  ))}
                <Col span={16}>
                  <SelectWithInputValue
                    placholder="Choose country"
                    options={remainingCountries}
                    onSave={({ selectField, numberField }) =>
                      addNationality({
                        country_id: selectField,
                        nationality_count: numberField,
                      })
                    }
                  />
                </Col>
              </Row>
            </div>
            <div className={styles.CompanyStats}>
              <p className={styles.CompanyStatsTitle}>
                Stats
                <p className={styles.CompanyDesc}>
                  Add any stats about your company you&apos;d like to share!
                  Example: <mark>44,000 Employees</mark>
                </p>
              </p>
              <Row gutter={[0, 12]}>
                {companyStats.map((stats) => (
                  <Col key={stats.id} span={18}>
                    <AddSelectOptionImage
                      onRemove={onRemoveStats}
                      options={companyStats}
                      value={stats.id}
                      title={stats.label}
                      isRemoveLoading={removeStatsLoadingId === stats.id}
                    ></AddSelectOptionImage>
                  </Col>
                ))}
                <Col span={18}>
                  <AddSelectOptionImage
                    options={companyStats}
                    onRemove={onRemoveStats}
                    onCustomAdd={onAddStats}
                    isSaveLoading={isSaveStatsLoading}
                    customPlaceholder="Add a custom stats"
                    isCustom
                  ></AddSelectOptionImage>
                </Col>
              </Row>
            </div>
            <Events
              title="Insights from company"
              label="Insight"
              events={companyInsights}
              onAddEvent={onAddInsight}
              onRemoveEvent={onRemoveInsight}
            ></Events>
          </div>
        ) : (
          <Spinner size="large" wrapper />
        )}
      </section>
    </>
  );
};

const CompanyWrapper = withAuth(Company);

// @ts-ignore
CompanyWrapper.getLayout = getLayout;

export default CompanyWrapper;
